import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, Loader2, Minus, Plus, ShoppingBasket, LogOut, CheckCircle2, Eye, Tag, X } from "lucide-react";
import ProductGallery from "@/components/ProductGallery";
import OrderFormButton from "@/components/OrderFormButton";
import ChildPicker, { childToStudent } from "@/components/ChildPicker";
import ShareOrderForm from "@/components/ShareOrderForm";
import logo from "@/assets/logo.png";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ecole-parent-token";
const ecoleApi = axios.create({ baseURL: BASE, withCredentials: true });
ecoleApi.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
export const euros = (c) => (Number(c || 0) / 100).toFixed(2);
export const bestDiscount = (p, qty, tiers, cartQty = qty) => {
  const ok = (tiers || []).filter((t) => t.min_quantity <= qty && ((t.scope === "product" && t.target_id === p.product_id) || (t.scope === "category" && t.target_id === p.category)));
  const cat = ok.filter((t) => t.scope === "category");
  const scoped = cat.length ? Math.max(...cat.map((t) => t.discount_percent)) : ok.reduce((m, t) => Math.max(m, t.discount_percent), 0);
  const glob = (tiers || []).filter((t) => t.scope === "global" && t.min_quantity <= cartQty).reduce((m, t) => Math.max(m, t.discount_percent), 0);
  return Math.max(scoped, glob);
};
const globalTiers = (tiers) => (tiers || []).filter((t) => t.scope === "global").sort((a, b) => a.min_quantity - b.min_quantity);
const productTiers = (p, tiers) => (tiers || [])
  .filter((t) => (t.scope === "product" && t.target_id === p.product_id) || (t.scope === "category" && t.target_id === p.category))
  .sort((a, b) => a.min_quantity - b.min_quantity);
export const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(", ") || fallback;
  return fallback;
};

export default function EcoleParent() {
  const { slug } = useParams();
  const [school, setSchool] = useState(null); // null loading, false not found
  const [authed, setAuthed] = useState(false);
  const [products, setProducts] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [needJoin, setNeedJoin] = useState(false);

  useEffect(() => {
    ecoleApi.get(`/ecoles/${slug}`).then(({ data }) => setSchool(data)).catch(() => setSchool(false));
  }, [slug]);

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const { data } = await ecoleApi.get(`/ecoles/${slug}/produits`);
      setProducts(data.products);
      setTiers(data.price_tiers || []);
      setAuthed(true);
      setNeedJoin(false);
    } catch (e) {
      setAuthed(false);
      if (e?.response?.status === 403) setNeedJoin(true);
      else localStorage.removeItem(TOKEN_KEY);
    } finally { setLoadingProducts(false); }
  };

  useEffect(() => {
    if (!school) return;
    if (localStorage.getItem(TOKEN_KEY)) { loadProducts(); return; }
    // Session déjà ouverte sur la boutique (même compte client)
    ecoleApi.get(`/auth/me`).then(({ data }) => { if (["customer", "parent"].includes(data.role)) loadProducts(); }).catch(() => {});
    // eslint-disable-next-line
  }, [school]);

  const logout = () => { ecoleApi.post(`/auth/logout`).catch(() => {}); localStorage.removeItem(TOKEN_KEY); setAuthed(false); setNeedJoin(false); setProducts([]); };

  if (school === null) return <Center><Loader2 className="animate-spin text-amber-deep" size={28} /> Chargement…</Center>;
  if (school === false) return <Center><p className="font-serif text-2xl text-bark">École introuvable ou fermée</p></Center>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10" data-testid="ecole-parent-page">
      <div className="mb-8 flex items-center gap-3">
        <img src={logo} alt="" className="h-12 w-12 object-contain" />
        <div>
          <p className="flex items-center gap-2 font-serif text-3xl font-semibold text-bark"><GraduationCap size={26} className="text-amber-deep" /> {school.name}</p>
          <p className="text-sm text-bark-soft">{school.city} · Commande groupée de miels — règlement par chèque, remise à l'école</p>
          {school.deadline && (
            <p data-testid="school-deadline" className={`mt-0.5 text-sm font-medium ${school.closed ? "text-terra" : "text-amber-deep"}`}>
              {school.closed ? "Campagne clôturée — les commandes ne sont plus acceptées." : `Commandes ouvertes jusqu'au ${new Date(school.deadline).toLocaleDateString("fr-FR")}`}
            </p>
          )}
        </div>
      </div>

      {needJoin ? (
        <JoinSchool slug={slug} schoolName={school.name} onJoined={loadProducts} onLogout={logout} />
      ) : !authed ? (
        <ParentAuth slug={slug} onAuthed={loadProducts} />
      ) : loadingProducts ? (
        <Center><Loader2 className="animate-spin text-amber-deep" size={26} /> Chargement de la boutique…</Center>
      ) : (
        <ParentShop slug={slug} products={products} tiers={tiers} onLogout={logout} closed={school.closed} schoolName={school.name} />
      )}
    </div>
  );
}

export function ProductPreviewModal({ product, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setFailed(false);
    ecoleApi.get(`/products/${product.product_id}`)
      .then(({ data }) => { if (active) setDetail(data); })
      .catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [product.product_id]);

  const d = detail || product;

  return (
    <div
      data-testid="product-preview-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-bark/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button data-testid="preview-close" onClick={onClose} aria-label="Fermer" className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-sand text-bark hover:bg-amber-800/10">
          <X size={18} />
        </button>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-bark-soft"><Loader2 size={22} className="animate-spin text-amber-deep" /> Chargement…</div>
        ) : (
          <>
            <ProductGallery product={d} fallback={logo} contain mainClass="max-h-72 rounded-2xl" />
            <p className="mt-4 font-serif text-2xl font-semibold text-bark">{d.name}</p>
            {(d.weight || product.weight) && <p className="mt-0.5 text-sm text-bark-soft">{d.weight || product.weight}</p>}
            <p className="mt-2 text-lg font-semibold text-amber-deep">{euros(d.price_cents ?? product.price_cents)} €</p>
            {d.origin && <p className="mt-3 text-sm text-bark-soft"><span className="font-medium text-bark">Origine :</span> {d.origin}</p>}
            {d.description && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-bark-soft">{d.description}</p>}
            {failed && !d.description && (
              <p data-testid="preview-blocked" className="mt-3 rounded-xl border border-terra/30 bg-terra/5 p-3 text-sm text-terra">
                Impossible d'afficher le détail du produit. Si votre navigateur bloque l'affichage, autorisez les fenêtres/contenus pour ce site, puis réessayez.
              </p>
            )}
            <p className="mt-5 rounded-xl bg-sage/5 p-3 text-xs text-bark-soft">
              Aperçu informatif. Pour commander, fermez cette fenêtre et ajustez les quantités directement sur la page de l'école.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-bark-soft">{children}</div>;
}

function JoinSchool({ slug, schoolName, onJoined, onLogout }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const join = async () => {
    if (!code.trim()) return toast.error("Saisissez le code d'accès de l'école");
    setBusy(true);
    try {
      await ecoleApi.post(`/ecoles/join`, { slug, access_code: code.trim() });
      toast.success(`Compte rattaché à ${schoolName}`);
      await onJoined();
    } catch (e) { toast.error(errMsg(e, "Code d'accès incorrect")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm" data-testid="join-school">
      <p className="font-serif text-xl text-bark">Rattacher votre compte à {schoolName}</p>
      <p className="mt-1 text-sm text-bark-soft">Vous êtes connecté avec votre compte Pétales de Miel. Saisissez le code d'accès communiqué par l'école pour commander ici.</p>
      <input data-testid="join-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code d'accès de l'école" className="mt-4 w-full rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
      <button data-testid="join-submit" onClick={join} disabled={busy} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep py-3 text-sm font-semibold text-white hover:bg-amber-deep/90 disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} Rejoindre l'école
      </button>
      <button data-testid="join-logout" onClick={onLogout} className="mt-3 w-full text-center text-xs text-bark-soft hover:text-terra">Utiliser un autre compte</button>
    </div>
  );
}

function ParentAuth({ slug, onAuthed }) {
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", access_code: "" });
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (mode === "register") {
      if (!f.name.trim()) return toast.error("Indiquez votre nom et prénom");
      if (!f.access_code.trim()) return toast.error("Saisissez le code d'accès de l'école");
      if (!f.email.trim()) return toast.error("Saisissez votre e-mail");
      if (f.password.length < 8) return toast.error("Le mot de passe doit contenir au moins 8 caractères");
    }
    setBusy(true);
    try {
      if (mode === "register") {
        const { data } = await ecoleApi.post(`/ecoles/register`, { slug, access_code: f.access_code.trim(), name: f.name.trim(), email: f.email.trim(), password: f.password });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        localStorage.setItem("ecole-parent-email", f.email.trim());
      } else {
        const { data } = await ecoleApi.post(`/auth/login`, { email: f.email.trim(), password: f.password });
        if (data.requires_2fa || !["customer", "parent"].includes(data.role)) throw { response: { data: { detail: "Utilisez votre compte client Pétales de Miel (boutique ou école)." } } };
        localStorage.setItem(TOKEN_KEY, data.access_token);
        localStorage.setItem("ecole-parent-email", f.email.trim());
      }
      await onAuthed();
    } catch (e) { toast.error(errMsg(e, "Impossible de continuer")); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-md rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm" data-testid="parent-auth">
      <div className="mb-5 flex rounded-full bg-sand p-1 text-sm font-medium">
        <button data-testid="parent-tab-login" onClick={() => setMode("login")} className={`flex-1 rounded-full py-2 ${mode === "login" ? "bg-amber-deep text-white" : "text-bark-soft"}`}>J'ai déjà un compte</button>
        <button data-testid="parent-tab-register" onClick={() => setMode("register")} className={`flex-1 rounded-full py-2 ${mode === "register" ? "bg-amber-deep text-white" : "text-bark-soft"}`}>Créer un compte</button>
      </div>
      <div className="space-y-3">
        {mode === "register" && (
          <>
            <input data-testid="parent-name" value={f.name} onChange={up("name")} placeholder="Votre nom et prénom" className="w-full rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
            <input data-testid="parent-code" value={f.access_code} onChange={up("access_code")} placeholder="Code d'accès de l'école" className="w-full rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
          </>
        )}
        <input data-testid="parent-email" type="email" value={f.email} onChange={up("email")} placeholder="Votre e-mail" className="w-full rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
        <input data-testid="parent-password" type="password" value={f.password} onChange={up("password")} placeholder="Mot de passe" className="w-full rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
        <button data-testid="parent-submit" onClick={submit} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep py-3 text-sm font-semibold text-white hover:bg-amber-deep/90 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : null} {mode === "register" ? "Créer mon compte et commander" : "Se connecter"}
        </button>
        {mode === "register" ? <p className="text-center text-xs text-bark-soft">Le code d'accès vous est communiqué par l'école.</p>
          : <p className="text-center text-xs text-bark-soft">Vous avez déjà un compte sur la boutique Pétales de Miel ? Utilisez les mêmes identifiants.</p>}
      </div>
    </div>
  );
}

function ParentShop({ slug, products, tiers, onLogout, closed, schoolName }) {
  const [qty, setQty] = useState({});
  const [student, setStudent] = useState({ first: "", last: "", klass: "", phone: "" });
  const [newsletter, setNewsletter] = useState(true);
  const [payment, setPayment] = useState("cheque");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [myOrders, setMyOrders] = useState([]);
  const [preview, setPreview] = useState(null);

  const [kids, setKids] = useState([]);
  const [kidId, setKidId] = useState(null);

  const loadMine = () => ecoleApi.get(`/ecoles/orders/mine`).then(({ data }) => setMyOrders(data.orders)).catch(() => {});
  const loadKids = (keepFirst = true) => ecoleApi.get(`/customer/children`).then(({ data }) => {
    const list = data.children.filter((c) => !c.school_id || c.school_id === data.school?.school_id || !data.school);
    setKids(list);
    if (keepFirst && list[0]) { setKidId(list[0].id); setStudent((st) => ({ ...st, ...childToStudent(list[0]) })); }
  }).catch(() => {});
  const pickKid = (c) => { setKidId(c ? c.id : null); setStudent((st) => ({ ...st, ...childToStudent(c) })); };
  useEffect(() => { loadMine(); loadKids(); }, []);

  const setQ = (pid, v) => setQty((q) => ({ ...q, [pid]: Math.max(0, v) }));
  const totalQty = useMemo(() => products.reduce((s, p) => s + (qty[p.product_id] || 0), 0), [qty, products]);
  const cart = useMemo(() => products.filter((p) => (qty[p.product_id] || 0) > 0).map((p) => {
    const q = qty[p.product_id];
    const disc = bestDiscount(p, q, tiers, totalQty);
    const unit = disc ? Math.round(p.price_cents * (100 - disc) / 100) : p.price_cents;
    return { ...p, q, disc, unit };
  }), [qty, products, tiers, totalQty]);
  const subtotal = cart.reduce((s, p) => s + p.unit * p.q, 0);
  const savings = cart.reduce((s, p) => s + (p.price_cents - p.unit) * p.q, 0);

  const submit = async () => {
    if (cart.length === 0) return toast.error("Ajoutez au moins un produit");
    if (!student.first.trim() || !student.last.trim()) return toast.error("Indiquez le prénom et le nom de l'élève");
    if (!student.klass.trim()) return toast.error("Indiquez la classe de l'élève");
    setBusy(true);
    try {
      const { data } = await ecoleApi.post(`/ecoles/orders`, {
        items: cart.map((p) => ({ product_id: p.product_id, qty: p.q })),
        student_first: student.first.trim(), student_last: student.last.trim(), student_class: student.klass.trim(), parent_phone: student.phone.trim(),
        payment_method: payment,
      });
      if (newsletter) {
        const email = localStorage.getItem("ecole-parent-email") || (myOrders[0]?.parent_email) || "";
        if (email) ecoleApi.post(`/newsletter`, { email, frequency: "monthly", origin_url: window.location.origin }).catch(() => {}); // best effort, non-bloquant
      }
      setDone(data);
      setQty({});
      loadMine();
      loadKids();
      toast.success("Commande enregistrée");
    } catch (e) { toast.error(errMsg(e, "Commande impossible")); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl border border-sage/40 bg-sage/5 p-8 text-center" data-testid="order-confirmation">
        <CheckCircle2 size={40} className="mx-auto text-sage" />
        <p className="mt-3 font-serif text-2xl text-bark">Merci, commande enregistrée !</p>
        <p className="mt-2 text-sm text-bark-soft">Montant : <strong>{euros(done.subtotal_cents)} €</strong> — à régler par chèque auprès de l'école. Les produits seront remis à l'école.</p>
        <button data-testid="order-again" onClick={() => setDone(null)} className="mt-5 rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white hover:bg-amber-deep/90">Passer une autre commande</button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-shop">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-serif text-xl text-bark">Choisissez vos douceurs</p>
        <div className="flex flex-wrap items-center gap-3">
        <p data-testid="parent-order-form-hint" className="max-w-xs text-xs leading-snug text-bark-soft sm:text-right">
          <strong className="font-semibold text-bark">Faites découvrir nos douceurs à vos proches !</strong> Imprimez le bon de commande ou envoyez-le en un clic à votre famille, vos amis ou vos collègues.
        </p>
        <OrderFormButton client={ecoleApi} path={`/ecoles/${slug}/bon-de-commande${kidId ? `?child_id=${kidId}` : ""}`} testid="parent-order-form-pdf" />
        <ShareOrderForm client={ecoleApi} slug={slug} child={kids.find((k) => k.id === kidId)} schoolName={schoolName} />
        <button data-testid="parent-logout" onClick={onLogout} className="flex items-center gap-1.5 text-sm text-bark-soft hover:text-terra"><LogOut size={14} /> Déconnexion</button>
        </div>
      </div>

      {closed && (
        <div data-testid="shop-closed-banner" className="rounded-2xl border border-terra/30 bg-terra/5 p-4 text-sm font-medium text-terra">
          La campagne de commande est clôturée. Vous pouvez consulter vos commandes mais plus en passer de nouvelles.
        </div>
      )}

      {globalTiers(tiers).length > 0 && (
        <div data-testid="global-tiers-banner" className="flex flex-wrap items-center gap-2 rounded-2xl border border-sage/30 bg-sage/5 p-4 text-sm">
          <Tag size={15} className="text-sage" />
          <span className="font-medium text-bark">Remise sur l'ensemble de votre commande ({totalQty} article{totalQty > 1 ? "s" : ""}) :</span>
          {globalTiers(tiers).map((t, i) => (
            <span key={i} className={`rounded-full px-2.5 py-0.5 text-xs ${totalQty >= t.min_quantity ? "bg-sage/20 font-semibold text-sage" : "bg-white text-bark-soft"}`}>
              dès {t.min_quantity} : −{t.discount_percent} %
            </span>
          ))}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => {
          const q = qty[p.product_id] || 0;
          const disc = bestDiscount(p, q, tiers, totalQty);
          const unit = disc ? Math.round(p.price_cents * (100 - disc) / 100) : p.price_cents;
          const pt = productTiers(p, tiers);
          return (
          <div key={p.product_id} data-testid={`shop-product-${p.product_id}`} className="flex flex-col gap-3 rounded-2xl border border-amber-800/15 bg-white p-3">
            <div className="flex items-start gap-3">
              <img src={p.image_url || logo} alt="" className="h-14 w-14 shrink-0 rounded-lg bg-white object-contain" />
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => setPreview(p)} data-testid={`shop-product-link-${p.product_id}`} className="flex w-full items-start gap-1 text-left text-sm font-medium text-bark hover:text-amber-deep">
                  <span className="hover:underline">{p.name}{p.weight ? ` - ${p.weight}` : ""}</span>
                  <Eye size={13} className="mt-0.5 shrink-0 opacity-60" />
                </button>
                <p className="mt-0.5 text-sm font-semibold text-amber-deep">
                  {disc > 0 ? (
                    <>
                      <span className="mr-1.5 text-xs font-normal text-stone-mute line-through">{euros(p.price_cents)} €</span>
                      {euros(unit)} € <span className="text-xs font-semibold text-sage">−{disc} %</span>
                    </>
                  ) : (
                    <>{euros(p.price_cents)} €</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-1">
              <button onClick={() => setQ(p.product_id, q - 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-amber-800/20 text-bark"><Minus size={13} /></button>
              <span data-testid={`shop-qty-${p.product_id}`} className="w-6 text-center text-sm font-semibold">{q}</span>
              <button data-testid={`shop-add-${p.product_id}`} onClick={() => setQ(p.product_id, q + 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-amber-deep text-amber-deep"><Plus size={13} /></button>
            </div>
            {pt.length > 0 && (
              <div data-testid={`shop-tiers-${p.product_id}`} className="flex flex-wrap items-center gap-1.5 border-t border-amber-800/10 pt-2 text-xs">
                <Tag size={11} className="text-sage" />
                {pt.map((t, i) => (
                  <span key={i} className={`rounded-full px-2 py-0.5 ${q >= t.min_quantity ? "bg-sage/15 font-semibold text-sage" : "bg-sand text-bark-soft"}`}>
                    dès {t.min_quantity} : −{t.discount_percent} %
                  </span>
                ))}
              </div>
            )}
          </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
        <p className="mb-3 flex items-center gap-2 font-serif text-lg font-semibold text-bark"><ShoppingBasket size={18} /> Votre commande — {euros(subtotal)} €</p>
        {savings > 0 && (
          <p data-testid="order-savings" className="-mt-2 mb-3 text-sm font-medium text-sage">Vous économisez {euros(savings)} € grâce aux remises par quantité.</p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><ChildPicker items={kids} selectedId={kidId} onPick={pickKid} /></div>
          <input data-testid="student-first" value={student.first} onChange={(e) => setStudent((s) => ({ ...s, first: e.target.value }))} placeholder="Prénom de l'élève *" className="rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
          <input data-testid="student-last" value={student.last} onChange={(e) => setStudent((s) => ({ ...s, last: e.target.value }))} placeholder="Nom de l'élève *" className="rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
          <input data-testid="student-class" value={student.klass} onChange={(e) => setStudent((s) => ({ ...s, klass: e.target.value }))} placeholder="Classe de l'élève * (ex. CE2)" className="rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
          <input data-testid="parent-phone" value={student.phone} onChange={(e) => setStudent((s) => ({ ...s, phone: e.target.value }))} placeholder="Votre téléphone" className="rounded-xl border border-amber-800/20 px-4 py-3 text-sm outline-none focus:border-amber-deep" />
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-bark"><input data-testid="newsletter-optin" type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} className="h-4 w-4 accent-amber-deep" /> Je souhaite recevoir la Gazette Champêtre (newsletter)</label>

        <div className="mt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-stone-mute">Mode de règlement</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="pay-cheque" onClick={() => setPayment("cheque")} className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${payment === "cheque" ? "border-amber-deep bg-amber-deep text-white" : "border-amber-800/20 text-bark"}`}>Chèque (remis à l'école)</button>
            <button type="button" data-testid="pay-card" disabled title="Disponible à l'ouverture des paiements en ligne" className="cursor-not-allowed rounded-full border border-amber-800/15 px-4 py-2 text-sm font-medium text-stone-mute opacity-60">Carte bancaire · bientôt</button>
          </div>
        </div>

        <button data-testid="order-submit" onClick={submit} disabled={busy || cart.length === 0 || closed} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep py-3 text-sm font-semibold text-white hover:bg-amber-deep/90 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : null} {closed ? "Campagne clôturée" : "Valider ma commande (règlement par chèque)"}
        </button>
      </div>

      {myOrders.length > 0 && (
        <div className="rounded-2xl border border-amber-800/15 bg-white p-5" data-testid="my-orders">
          <p className="mb-2 font-serif text-lg font-semibold text-bark">Mes commandes</p>
          <ul className="space-y-2 text-sm">
            {myOrders.map((o) => (
              <li key={o.order_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-sand/50 px-3 py-2">
                <span className="text-bark">{o.student_first} {o.student_last} · {o.student_class}</span>
                <span className="text-bark-soft">{euros(o.subtotal_cents)} € · {o.status === "pending_payment" ? "en attente de paiement" : o.status === "delivered" ? "remise" : o.status === "paid" ? "payée" : o.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview && <ProductPreviewModal product={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
